import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { UserService } from '../../services/user.service';
import { SummaryService, LogSummaryDTO } from '../../services/summary.service';
import { TableType } from '../../types/table-types.type';
import { LogType } from '../../types/log-types.type';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { TableModule } from 'primeng/table';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Subject, takeUntil, finalize, timeout, catchError } from 'rxjs';
import { of } from 'rxjs';


@Component({
  selector: 'app-summary',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    CalendarModule,
    TableModule,
    ProgressSpinnerModule,
  ],
  template: `
<p-card>

              <ng-template pTemplate="header">
                <div class="card-header">
                <h2 class="card-title"><i class="pi pi-chart-bar"></i>Resumo de Ações</h2>
                </div>
            </ng-template>

  <div *ngIf="isShowing" class="spinner-overlay">
    <p-progressSpinner styleClass="custom-spinner" strokeWidth="4"></p-progressSpinner>
    <p class="loading-text">Calculando...</p>
  </div>

  <div class="admin-logs-container">
    <form [formGroup]="filterForm" (ngSubmit)="searchLogs()">
      <div class="filter-inputs">
        <div class="form-field">
          <label class="field-label">Data Inicial</label>
          <p-calendar
            formControlName="initialDate"
            dateFormat="dd/mm/yy"
            [showIcon]="true"
            placeholder="Selecione a data"
            styleClass="w-full">
          </p-calendar>
        </div>

        <div class="form-field">
          <label class="field-label">Data Final</label>
          <p-calendar
            formControlName="finalDate"
            dateFormat="dd/mm/yy"
            [showIcon]="true"
            placeholder="Selecione a data"
            styleClass="w-full">
          </p-calendar>
        </div>

        <div class="form-field">
          <label class="field-label">Usuário</label>
          <p-dropdown
            formControlName="userId"
            [options]="users"
            optionLabel="name"
            optionValue="id"
            placeholder="Selecione o usuário"
            [filter]="true"
            filterBy="name"
            styleClass="w-full"
            [showClear]="true">
          </p-dropdown>
        </div>

        <div class="form-field">
          <label class="field-label">Tabela</label>
          <p-dropdown
            formControlName="table"
            [options]="tableOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Selecione a tabela"
            styleClass="w-full"
            [showClear]="true">
          </p-dropdown>
        </div>

        <div class="form-field">
          <label class="field-label">Ação</label>
          <p-dropdown
            formControlName="action"
            [options]="actionOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Selecione a ação"
            styleClass="w-full"
            [showClear]="true">
          </p-dropdown>
        </div>

        <div class="form-field">
          <label class="field-label">Período</label>
          <p-dropdown
            formControlName="period"
            [options]="periodOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Selecione o período"
            styleClass="w-full">
          </p-dropdown>
        </div>

        <div class="form-field button-field">
          <p-button
            label="Buscar"
            icon="pi pi-search"
            type="submit"
            styleClass="search-button"
            [style]="{'width': '100%'}">
          </p-button>
        </div>
      </div>
    </form>

    <div class="results-container" *ngIf="logData.length > 0">
      <p-table
        [value]="logData"
        [tableStyle]="{'min-width': '50rem'}"
        styleClass="p-datatable-sm p-datatable-striped">
        <ng-template pTemplate="header">
          <tr>
            <th>Período</th>
            <th>Usuário</th>
            <th>Tabela</th>
            <th>Ação</th>
            <th>Total</th>
            <th>Fonte</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-element>
          <tr>
            <td>{{ formatPeriod(element.period) || '—' }}</td>
            <td>{{ element.userShortName || 'N/A' }}</td>
            <td>{{ element.tableName || '—' }}</td>
            <td>{{ element.actionName || '—' }}</td>
            <td>{{ element.total ?? 0 }}</td>
            <td>{{ element.source === 'LEGACY' ? 'Legado' : element.source === 'NEW' ? 'Novo' : '—' }}</td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
              <td colspan="6" class="empty-message">
              Nenhum registro encontrado
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  </div>
</p-card>
  `,
  styles: [
    `
    .card-title {
      color: #325565;
      font-size: 1.5rem;
      font-weight: 600;
      margin: 0;
      padding: 1rem;
    }

        .card-header {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 1rem 1rem 0.25rem 1rem;
        }

        .card-header h2 {
            margin: 0;
            color: var(--primary-color);
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }

    .admin-logs-container {
      padding: 0.5rem 1rem 1rem 1rem;
    }

    .filter-inputs {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1rem;
      margin-bottom: 2rem;
    }

    .form-field {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .field-label {
      color: #325565;
      font-weight: 500;
      font-size: 0.875rem;
    }

    .button-field {
      display: flex;
      align-items: flex-end;
      padding-top: 1.75rem;
    }

    ::ng-deep .search-button {
      background-color: rgb(16, 148, 158) !important;
      border-color: rgb(16, 148, 158) !important;
      color: white !important;
      height: 42px !important;
    }

    ::ng-deep .search-button:hover {
      background-color: rgb(6, 182, 212) !important;
      border-color: rgb(6, 182, 212) !important;
    }

    ::ng-deep .w-full {
      width: 100%;
    }

    ::ng-deep .p-calendar,
    ::ng-deep .p-dropdown {
      height: 42px;
    }

    ::ng-deep .p-calendar .p-inputtext,
    ::ng-deep .p-dropdown .p-dropdown-label {
      height: 42px;
      display: flex;
      align-items: center;
    }

    ::ng-deep .p-calendar .p-datepicker-trigger {
      height: 42px;
    }

    .results-container {
      margin-top: 2rem;
      overflow-x: auto;
    }

    ::ng-deep .p-datatable {
      border: 1px solid #e5e7eb;
      border-radius: 0.5rem;
    }

    ::ng-deep .p-datatable .p-datatable-thead > tr > th {
      background-color: transparent;
      color: #325565;
      font-weight: 600;
      padding: 1rem;
      text-align: left;
      border-bottom: 2px solid #e5e7eb;
    }

    ::ng-deep .p-datatable .p-datatable-tbody > tr > td {
      padding: 0.75rem 1rem;
      color: #374151;
    }

    ::ng-deep .p-datatable .p-datatable-tbody > tr:hover {
      background-color: #f9fafb;
    }

    .empty-message {
      text-align: center;
      color: #6b7280;
      padding: 2rem !important;
      font-style: italic;
    }

    .spinner-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 1000;
      gap: 1rem;
    }

    .loading-text {
      color: white;
      font-size: 1.125rem;
      font-weight: 500;
      margin: 0;
    }

    ::ng-deep .custom-spinner {
      width: 60px;
      height: 60px;
    }

    ::ng-deep .custom-spinner .p-progress-spinner-circle {
      stroke: #F15A22;
    }

    @media (max-width: 768px) {
      .filter-inputs {
        grid-template-columns: 1fr;
      }

      .results-container {
        overflow-x: scroll;
      }
    }
    `,
  ],
})
export class SummaryComponent implements OnInit, OnDestroy {
  private destroyed$ = new Subject<void>();
  displayedColumns: string[] = ['period', 'user', 'table', 'action', 'total', 'source'];
  logData: LogSummaryDTO[] = [];
  users: { id: number; name: string }[] = [];
  filterForm: FormGroup;

  isShowing: boolean = false;

  // Mapear as opções das enums para o formato usado no template
  tableOptions = [
    { value: '', label: '' }, // Opção vazia
    ...Object.keys(TableType).map((key) => ({
      value: key, // Enviar o código do enum (ex: "ADDRESS")
      label: TableType[key as keyof typeof TableType], // Exibir a descrição associada (ex: "Endereço")
    })),
  ];

  actionOptions = [
    { value: '', label: '' }, // Opção vazia
    ...Object.keys(LogType).map((key) => ({
      value: key, // Enviar o código do enum (ex: "ADDED")
      label: LogType[key as keyof typeof LogType], // Exibir a descrição associada (ex: "Cadastro")
    })),
  ];

  // Exemplo de como pegar o valor ao selecionar uma opção
  onTableSelectionChange(selectedTable: string) {
    const selectedEnumValue =
      TableType[selectedTable as keyof typeof TableType];
    //console.log('Valor selecionado:', selectedEnumValue); // Enviar para o backend
  }

  onActionSelectionChange(selectedAction: string) {
    const selectedEnumValue = LogType[selectedAction as keyof typeof LogType];
    //console.log('Valor selecionado:', selectedEnumValue); // Enviar para o backend
  }

  periodOptions = [
    { value: 'dia', label: 'Diário' },
    { value: 'mes', label: 'Mensal' },
    { value: 'ano', label: 'Anual' },
  ];

  constructor(
    private summaryService: SummaryService,
    private userService: UserService,
    private fb: FormBuilder
  ) {
    this.filterForm = this.fb.group({
      initialDate: [''],
      finalDate: [''],
      userId: [''],
      table: [''],
      action: [''],
      period: ['dia'],
    });
  }

  ngOnInit() {
    this.loadUsers();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  // In your SummaryComponent
  loadUsers() {
    this.userService.getUsers()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          // Assuming response.data contains the array of users
          if (response && response.data) {
            this.users = response.data
              .map((user) => ({
                id: user.id,
                name: user.shortName,
              }))
              .sort((a, b) => a.name.localeCompare(b.name));
          }
        },
        error: (error) => {
          console.error('Error loading users', error);
        },
      });
  }

  searchLogs() {
    if (this.filterForm.invalid) return;

    this.isShowing = true; // Ativa o spinner
    this.logData = []; // Limpa dados anteriores

    const formValue = this.filterForm.value;
    
    // Converter datas para formato esperado pela API (se necessário)
    const initialDate = formValue.initialDate ? this.formatDate(formValue.initialDate) : '';
    const finalDate = formValue.finalDate ? this.formatDate(formValue.finalDate) : '';
    
    this.summaryService
      .getSummary(
        initialDate,
        finalDate,
        formValue.userId,
        formValue.table,
        formValue.action,
        formValue.period
      )
      .pipe(
        takeUntil(this.destroyed$),
        timeout(60000), // Timeout de 60 segundos
        catchError((error: any) => {
          console.error('Error fetching logs:', error);
          if (error.name === 'TimeoutError') {
            console.error('Request timed out after 60 seconds');
          }
          return of([] as LogSummaryDTO[]); // Retorna array vazio em caso de erro
        }),
        finalize(() => {
          // Garante que o spinner será desligado em QUALQUER cenário
          this.isShowing = false;
        })
      )
      .subscribe({
        next: (data: LogSummaryDTO[]) => {
          console.log('Dados recebidos do backend:', JSON.stringify(data, null, 2));
          this.logData = data;
        },
        error: (error: any) => {
          // Este bloco pode não ser executado se catchError já tratou o erro
          console.error('Error in subscribe:', error);
        },
      });
  }

  formatPeriod(period: string): string {
    if (!period) return '';

    const selectedPeriod = this.filterForm.get('period')?.value || 'dia';

    switch (selectedPeriod) {
      case 'dia':
        // Formato backend: YYYY-MM-DD → DD/MM/YYYY
        return this.parseAndFormatDate(period, 'DD/MM/YYYY');
      case 'mes':
        // Formato backend: YYYY-MM → MM/YYYY
        return this.parseAndFormatMonth(period);
      case 'ano':
        // Já é o ano
        return period;
      default:
        return period;
    }
  }

  private parseAndFormatDate(value: string, targetFormat: string): string {
    // Tenta parse YYYY-MM-DD
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      if (targetFormat === 'DD/MM/YYYY') {
        return `${day}/${month}/${year}`;
      }
    }
    return value;
  }

  private parseAndFormatMonth(value: string): string {
    // Tenta parse YYYY-MM
    const match = value.match(/^(\d{4})-(\d{2})/);
    if (match) {
      const [, year, month] = match;
      return `${month}/${year}`;
    }
    return value;
  }

  private formatDate(date: Date): string {
    if (!date) return '';
    
    // PrimeNG Calendar retorna um objeto Date
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  }
}
